import { useEffect, useMemo, useState } from "react";

import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

import styles from "./react-table.module.css";
import SimpleDropDown from "components/UI/SimpleDropDown";
import { camelCaseToWords } from "utils";
import Checkbox from "components/UI/Form/Checkbox";
import ReactTableFooterNav from "./ReactTableFooterNav";
import { CogIcon } from "@heroicons/react/outline";

interface IReactTableProps {
  cols: any;
  data: any;
  onRowClick?: (row: any) => void;
  fetching?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    pageCount: number;
    currentPage: number;
    setPageSize: (v: number) => void;
    nextPage: () => void;
    previousPage: () => void;
  };

  headerBorderAccent?: boolean;
  height?: string;
  onRowSelect?: (val: any[]) => void;
}

const ReactTable = ({
  cols,
  data,
  pagination,
  onRowClick,
  onRowSelect,
  fetching,
  headerBorderAccent,
  height = "h-full",
}: IReactTableProps) => {
  const columns = useMemo(
    () => [
      {
        id: "rowSelect",
        maxSize: 10,
        header: (d: any) => (
          <Checkbox
            id="react-table-column-checkbox"
            name="ReactTableColumnCheckbox"
            checked={d.table.getIsAllRowsSelected()}
            onChange={d.table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: (c: any) => (
          <Checkbox
            id="react-table-column-checkbox"
            name="ReactTableColumnCheckbox"
            checked={c.row.getIsSelected()}
            onChange={c.row.getToggleSelectedHandler()}
          />
        ),
      },
      ...cols,
    ],
    [cols]
  );
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});

  const table = useReactTable({
    data,
    columns,
    pageCount: pagination?.pageCount,
    state: {
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnResizing: true,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    if (onRowSelect && table.getSelectedRowModel().flatRows) {
      onRowSelect(table.getSelectedRowModel().flatRows);
    }
  }, [table.getSelectedRowModel().flatRows, onRowSelect]);

  return (
    <>
      <div className={`overflow-auto`}>
        <div
          {...{
            className:
              "relative divide-y divide-gray-200 border overflow-hidden rounded-xl",
            style: {
              width: table.getCenterTotalSize(),
            },
          }}
        >
          <div className="z-20 absolute top-3 right-4 text-neutralColor-normal cursor-pointer">
            <SimpleDropDown
              itemContainerWidth="w-max"
              toggle={<CogIcon className="w-5 h-5" />}
            >
              <div className="px-4 py-2 space-y-1 w-full">
                <span className="font-semibold">Columns</span>
                {table
                  .getAllLeafColumns()
                  .filter((c) => c.id !== "rowSelect")
                  .map((column) => {
                    return (
                      <div key={column.id}>
                        <label className="capitalize flex">
                          <Checkbox
                            id="react-table-column-checkbox"
                            name="ReactTableColumnCheckbox"
                            checked={column.getIsVisible()}
                            onChange={column.getToggleVisibilityHandler()}
                          />
                          <span className="-ml-3">
                            {camelCaseToWords(column.id)}
                          </span>
                        </label>
                      </div>
                    );
                  })}
              </div>
            </SimpleDropDown>
          </div>

          <table>
            <thead
              className={`bg-gray-50 ${headerBorderAccent && "border-l-4"}`}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="w-full">
                  {headerGroup.headers.map((header) => (
                    <th
                      {...{
                        key: header.id,
                        className:
                          "relative p-6 text-left text-xs font-bold text-gray-500 capitalize tracking-wider",
                        colSpan: header.colSpan,
                        style: {
                          width: header.getSize(),
                        },
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}

                      <div
                        {...{
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `${styles["resizer"]} ${
                            header.column.getIsResizing()
                              ? styles["isResizing"]
                              : ""
                          }`,
                        }}
                      />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {fetching && (
              <tbody className="bg-white divide-y divide-gray-200 animate-pulse">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <tr key={num}>
                    {Array.from(Array(columns.length).keys()).map((num) => (
                      <td className="p-6">
                        <span className="h-2 rounded-md bg-neutralColor-light w-32 block"></span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
            {!fetching && (
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr
                      key={row.id}
                      className="hover:bg-green-50 cursor-pointer"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          {...{
                            key: cell.id,
                            className: "px-6 py-4",
                            style: {
                              width: cell.column.getSize(),
                            },
                          }}
                          onClick={() =>
                            onRowClick &&
                            cell.column.id !== "rowSelect" &&
                            cell.column.id !== "actions" &&
                            onRowClick(row.original)
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
          <div>
            {pagination && (
              <ReactTableFooterNav
                nextPage={pagination.nextPage}
                previousPage={pagination.previousPage}
                currentPage={pagination.currentPage}
                totalPage={table.getPageCount() || 1}
                perPage={pagination.pageSize}
                perPageHandler={pagination.setPageSize}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReactTable;
